import { html } from 'lit';
import { editorGridDefaultColumns } from '../../geometry.js';
import type { WebEditorElementFrame, WebEditorLayoutProject } from '../../layout.js';
import type { WebEditorElementDefinition } from '../../element-definition.js';
import type { EditorFrame } from './editorPageConfig.js';
import type { EditorGeometryOperations, EditorResizeHandle } from './editorGeometryOperations.js';
import type { EditorGeometryState } from './editorGeometryState.js';

interface GeometryOverlayOptions {
  definition?: WebEditorElementDefinition;
  frame: WebEditorElementFrame;
  project: WebEditorLayoutProject;
  activeFrame: EditorFrame;
  state: EditorGeometryState;
  operations: EditorGeometryOperations;
}

const resizeHandles: readonly EditorResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const imageScaleHandles: readonly EditorResizeHandle[] = ['nw', 'ne', 'se', 'sw'];
const resizeHandleLabel: Record<EditorResizeHandle, string> = {
  n: 'Bildhöhe oben ändern',
  ne: 'Bild proportional oben rechts skalieren',
  e: 'Bildbreite rechts ändern',
  se: 'Bild proportional unten rechts skalieren',
  s: 'Bildhöhe unten ändern',
  sw: 'Bild proportional unten links skalieren',
  w: 'Bildbreite links ändern',
  nw: 'Bild proportional oben links skalieren',
};

export const renderEditorGeometryOverlay = (options: GeometryOverlayOptions) => {
  const rect = options.state.overlayRect;
  const definition = options.definition;
  const elementId = definition?.id;
  if (!rect || !definition || !elementId || rect.elementId !== elementId) return '';
  const unlocked = options.state.isUnlocked(elementId);
  const isImage = definition.kind === 'image';
  const itemFrame = options.project.elementFrames[definition.maskHostId ?? elementId];
  const isAutoItem = Boolean(options.activeFrame === 'desktop' ? itemFrame?.autoLayoutItemDesktop : itemFrame?.autoLayoutItemMobile);
  const properties = options.activeFrame === 'desktop'
    ? ({ grid: 'gridDesktop', columns: 'gridColumnsDesktop', focus: 'imageFocusDesktop', mask: 'imageMaskDesktop' } as const)
    : ({ grid: 'gridMobile', columns: 'gridColumnsMobile', focus: 'imageFocusMobile', mask: 'imageMaskMobile' } as const);
  const columns = options.frame[properties.columns] ?? editorGridDefaultColumns;
  const focus = options.frame[properties.focus] ?? options.frame.imageFocusDesktop ?? { x: 50, y: 50 };
  const focusMode = isImage && options.frame.objectFit !== 'fill' && options.state.focusEditingElementId === elementId;
  const imageMode = options.state.imageEditingMode;
  const maskEnabled = isImage || Boolean(definition.maskHostId) || (options.frame[properties.mask] ?? options.frame.imageMaskDesktop ?? false);
  const hasLegacyOffset = Boolean(options.frame.x || options.frame.y);
  const nudge = (event: KeyboardEvent) => {
    if (!unlocked || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const step = event.shiftKey ? 3 : 1;
    if (event.key === 'ArrowLeft') options.operations.nudgePlacement(elementId, -step, 0);
    if (event.key === 'ArrowRight') options.operations.nudgePlacement(elementId, step, 0);
    if (event.key === 'ArrowUp') options.operations.nudgePlacement(elementId, 0, -step);
    if (event.key === 'ArrowDown') options.operations.nudgePlacement(elementId, 0, step);
  };

  return html`
    <div
      class="vp-editor-geometry-overlay ${unlocked ? 'is-unlocked' : 'is-locked'} ${focusMode ? 'is-focus-mode' : ''} ${isImage ? `is-${imageMode}-edit-mode` : ''} ${definition.kind === 'text' || definition.kind === 'icon' ? 'is-inline-toolbar-selection' : ''}"
      style=${`left: ${rect.left}px; top: ${rect.top}px; width: ${rect.width}px; height: ${rect.height}px;`}
      aria-hidden="false"
    >
      ${unlocked && !isAutoItem
        ? html`<div class="vp-editor-geometry-grid" style=${`--vp-editor-overlay-columns: ${columns}`} aria-hidden="true">
            ${Array.from({ length: columns }, () => html`<i></i>`)}
          </div>`
        : ''}
      <div class="vp-editor-geometry-toolbar" role="toolbar" aria-label="Geometrie">
        <button
          type="button"
          class=${unlocked ? 'is-active' : ''}
          title=${unlocked ? 'Element sperren' : 'Element entsperren'}
          aria-label=${unlocked ? 'Element sperren' : 'Element entsperren'}
          aria-pressed=${unlocked ? 'true' : 'false'}
          @pointerdown=${(event: PointerEvent) => event.stopPropagation()}
          @click=${() => options.state.toggleLock(elementId)}
        >
          <span class=${unlocked ? 'icon-[material-symbols--lock-open-right-outline]' : 'icon-[material-symbols--lock-outline]'}></span>
          ${isImage ? '' : html`<small>${unlocked ? 'Entsperrt' : 'Gesperrt'}</small>`}
        </button>
        ${isImage
          ? html`<span class="vp-editor-overlay-mode-switch" role="group" aria-label="Bild oder Layoutgröße bearbeiten">
              <button type="button" class=${imageMode === 'image' ? 'is-active' : ''} title="Bildausschnitt zoomen" aria-label="Bildausschnitt zoomen" aria-pressed=${String(imageMode === 'image')} @click=${() => options.state.setImageEditingMode('image')}><span class="icon-[material-symbols--image-outline]" aria-hidden="true"></span></button>
              <button type="button" class=${imageMode === 'mask' ? 'is-active' : ''} title="Layoutgröße bearbeiten" aria-label="Layoutgröße bearbeiten" aria-pressed=${String(imageMode === 'mask')} @click=${() => { options.state.setFocusEditingElementId(null); options.state.setImageEditingMode('mask'); }}><span class="icon-[material-symbols--crop]" aria-hidden="true"></span></button>
            </span>`
          : ''}
        <button
          type="button"
          class="vp-editor-geometry-move"
          title=${hasLegacyOffset ? 'Alten Versatz zuerst übernehmen' : isAutoItem ? 'Im Auto Layout neu anordnen' : 'Auf Raster verschieben'}
          aria-label=${isAutoItem ? 'Element im Auto Layout neu anordnen' : 'Element auf Raster verschieben'}
          ?disabled=${!unlocked || hasLegacyOffset}
          @pointerdown=${(event: PointerEvent) => options.operations.startMove(event, elementId)}
          @keydown=${nudge}
        >
          <span class="icon-[material-symbols--open-with]" aria-hidden="true"></span>
        </button>
      </div>
      ${isImage && unlocked && !hasLegacyOffset && !focusMode && (imageMode === 'image' || maskEnabled)
        ? (imageMode === 'image' ? imageScaleHandles : resizeHandles).map(
            handle => html`<button
              type="button"
              class=${imageMode === 'image' ? 'vp-editor-resize-handle is-image-scale-handle' : 'vp-editor-resize-handle'}
              data-handle=${handle}
              aria-label=${imageMode === 'image' ? 'Bild proportional skalieren' : resizeHandleLabel[handle]}
              title=${imageMode === 'image' ? 'Bild proportional skalieren' : resizeHandleLabel[handle]}
              @pointerdown=${(event: PointerEvent) => imageMode === 'image'
                ? options.operations.startImageScale(event, elementId, handle)
                : options.operations.startResize(event, elementId, handle)}
            ></button>`,
          )
        : ''}
      ${focusMode
        ? html`
            <button
              type="button"
              class="vp-editor-image-focus-surface"
              aria-label="Bildfokus durch Ziehen positionieren"
              @pointerdown=${(event: PointerEvent) => options.operations.startFocus(event, elementId)}
            >
              <span
                class="vp-editor-image-focus-marker"
                style=${`left: ${focus.x}%; top: ${focus.y}%;`}
                aria-hidden="true"
              ></span>
            </button>
          `
        : ''}
    </div>
  `;
};
